import { API_BASE, authHeaders } from './apiAuth'
import type { ImageRef } from './imageRefs'

/**
 * The board's Angle card (Worker: routes/angles.ts, Huanrui 2026-10-09).
 * One picture and where to look at it from; the run is waited on with
 * awaitRun like any picture run.
 *
 * FLUX 2 with fal's multiple-angles LoRA, at about two megapixels:
 * $0.021 a megapixel, so about $0.042 -- 42 credits -- a picture.
 */
export const ANGLE_CREDITS = 42

export interface AngleAsk {
  projectId: string
  image: ImageRef
  /** 0 front, 90 right side, 180 back, 270 left side. */
  horizontal: number
  /** 0 eye level, 30 raised, 60 from well above. */
  vertical: number
  /** 0 wide, 5 medium, 10 close. */
  zoom: number
}

async function reason(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    if (body?.error) return body.error
  } catch {
    /* not JSON */
  }
  return `HTTP ${response.status}`
}

export async function startAngle(ask: AngleAsk): Promise<{ runId: string; live: number; limit: number }> {
  const response = await fetch(`${API_BASE}/api/canvas-angle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(ask),
  })
  if (!response.ok) throw new Error(await reason(response))
  const body = (await response.json()) as { runId?: string; live?: number; limit?: number }
  if (!body.runId) throw new Error('The run did not start')
  return { runId: body.runId, live: Number(body.live) || 0, limit: Number(body.limit) || 0 }
}

/** Where the camera is, in words: "45° round · 20° up · medium". */
export function angleWords(horizontal: number, vertical: number, zoom: number): string {
  const distance = zoom <= 2.5 ? 'wide' : zoom >= 7.5 ? 'close' : 'medium'
  return `${Math.round(horizontal)}° round · ${Math.round(vertical)}° up · ${distance}`
}
